import { lazy, useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import styles from './index.module.scss';
import { useStores } from '@/app/csr-src/models';
import ArrowRight from '@/assets/icons/ArrowRight.svg';
import Edit from '@/assets/icons/review_edit.svg';
import Nut from '@/assets/icons/editicon.svg';
import OrderIcon from '@/assets/icons/OrderIcon.svg';
import VoucherIcon from '@/assets/icons/VoucherIcon.svg';
import WishIcon from '@/assets/icons/WishIcon.svg';
import AddressIcon from '@/assets/icons/AddressIcon.svg';
import HistoryIcon from '@/assets/icons/HistoryIcon2.svg';
import MemberPlusIcon from '@/assets/icons/member_plus_icon.svg';
import MemberLogo from '@/assets/icons/memberLogoNew.svg';
import PointsIcon from '@/assets/icons/points.svg';
import JumpArrow from '@/app/membership/assets/images/jumpArrow.svg';

import { getThirdAppAuthorizeUrl } from '@/apis/login/login.api';
import { MemberInfoVO } from '@/apis/membership/membership.interface';
import { SceneCode } from '@/apis/recommend/recommend.interface';
import { fetchGetActivityApi } from '@/app/csr-src/apis/invitePolitely/invitePolitely.api';
import { getUserInfoApi } from '@/app/csr-src/apis/personal/index.api';

import {
  fetchBindThirdPartyAccount,
  fetchGetOrderStatisInfo,
  fetchThirdPartyAccount,
  fetchUserCenterAccount,
} from '@/app/csr-src/apis/profile/profile.api';
import type { UserAccountType } from '@/app/csr-src/apis/profile/profile.interface';
import { BindThirdPartyAccountVO } from '@/app/csr-src/apis/profile/profile.interface';
import LinkTips from '@/app/csr-src/pages/profile/linkTips/index';
import AccountSetDialog from '@/app/csr-src/pages/profile/personalCenter/personalAccountSetDialog/index';
import InfoSetDialog from '@/app/csr-src/pages/profile/personalCenter/personalInfoSetDialog/index';
import CDNImage from '@/components/cdnImage/cdnImage';
import UKToast from '@/components/UKToast';
import LoginType from '@/constants/loginConfig';
import { EXPOSURE_RECORD, sendClickTrack, sendPv } from '@/tracks/25118';
import {
  EXPOSURE_RECORD as MEMBER_EXPOSURE_RECORD,
  sendClickTrack as MEMBER_SENDClICKTRACK,
} from '@/tracks/32416';
import {
  EXPOSURE_RECORD as user_EXPOSURE_RECORD,
  sendClickTrack as user_sendClickTrack,
} from '@/tracks/25354';
import { imageUtil } from '@/utils/imageUtil';
import _ from 'lodash';
import { useRouter, useSearchParams } from 'next/navigation';
import { MEMBER_STATUS } from '@/constants/membership';

const RecommendClient = lazy(() => import('@/components/recommend/recommendClient'));

interface UserInfo {
  imageUrl?: string;
  nickName?: string;
  userTag?: any;
  extInfo?: any;
}

interface Item {
  icon: React.ReactNode;
  name: string;
  num?: number;
  subTitle?: string;
  onClick: () => void;
}

type OrderInfoStaticType = {
  afterSaleCount: number;
  unpaidCount: number;
  waitCommentCount: number;
  waitRecCount: number;
};

const Profile = observer(() => {
  const { t } = useTranslation();
  const router = useRouter();
  const [userInfo, setUserInfo] = useState<UserInfo | null>({});
  const [couponNum, setCouponNum] = useState<number>(0); // 未使用优惠券数
  const navigate = useNavigate();
  const [infoOpen, setInfoOpen] = useState(false);
  const [accountSetOpen, setAccountSetOpen] = useState(false);
  const { orderStore } = useStores();
  const [showActivity, setShowActivity] = useState(false);
  const [activityImage, setActivityImage] = useState('');
  const [activityUrl, setActivityUrl] = useState('');
  const [orderInfoStatic, setOrderInfoStatic] = useState<OrderInfoStaticType>();
  const [openDrawer, setOpenDrawer] = useState(false);
  const [memberInfo, setMemberInfo] = useState<MemberInfoVO>();
  const [isMember, setIsMember] = useState(false); //是否是会员
  const [showMember, setShowMember] = useState(false); //是否显示会员功能
  const [isNeedBind, setIsNeedBind] = useState(false); //是否可以绑定三方账号
  const [linkThirdUrl, setLinkThirdUrl] = useState<string>(''); // 关联三方账号地址
  const [bindThirdParty, setBindThirdParty] = useState<boolean>(false);

  const origin = (window as any).location.origin;
  const searchParams = useSearchParams();
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const itemsList: Item[] = [
    {
      icon: (
        <VoucherIcon
          exptag={'exp|' + EXPOSURE_RECORD.MyJD_Main_Coupons_Expo}
          data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
          className={styles.icon}
          width={24}
          height={24}
        />
      ),
      name: t('common.pageName.coupons'),
      subTitle: userInfo?.extInfo?.newUser_couponText
        ? `<span style="color:#CC0C1C"}>${userInfo?.extInfo?.newUser_couponText}</span>`
        : t(couponNum > 0 ? 'profile.page.couponsSubTitle' : 'profile.page.uncouponsSubTitle'),
      onClick: () => {
        sendClickTrack.MyJD_Main_Coupons();
        navigate('/profile/coupons-discounts');
      },
    },
    {
      icon: (
        <WishIcon
          exptag={'exp|' + EXPOSURE_RECORD.MyJD_Main_WishList_Expo}
          data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
          className={styles.icon}
          width={24}
          height={24}
        />
      ),
      name: t('common.pageName.wishlist'),
      subTitle: t('profile.page.wishListSubTitle'),
      onClick: () => {
        sendClickTrack.MyJD_Main_WishList();
        navigate('/profile/wishlist');
      },
    },
    {
      icon: (
        <PointsIcon
          exptag={'exp|' + EXPOSURE_RECORD.MyJD_Main_History_Expo}
          data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
          className={styles.icon}
          width={24}
          height={24}
        />
      ),
      name: t('points.points'),
      subTitle: t('points.pointsSubTitle'),
      onClick: () => {
        sendClickTrack.MyJD_Main_History();
        navigate('/profile/points-activity');
      },
    },
  ];

  // 点击个人编辑
  const handleClickEditProfile = () => {
    sendClickTrack.MyJD_Main_Name();
    setInfoOpen(true);
  };
  // 点击个人账户
  const handleClickAccount = () => {
    sendClickTrack.MyJD_Main_Settings();
    setAccountSetOpen(true);
  };
  // 点击订单
  const handleClickMyOrders = (key: string, num?: number) => {
    switch (key) {
      case 'order':
        sendClickTrack.MyJD_Main_Orders();
        orderStore.setOrderType('4096');
        navigate('/profile/my-orders');
        break;
      case 'unpaid':
        sendClickTrack.MyJD_Main_Orders_StatusBtn({
          icon_name: 'unpaid',
        });
        orderStore.setOrderType('1');
        navigate('/profile/my-orders');
        break;
      case 'pending':
        sendClickTrack.MyJD_Main_Orders_StatusBtn({
          icon_name: 'pending',
        });
        orderStore.setOrderType('128');
        navigate('/profile/my-orders');
        break;
      case 'review':
        sendClickTrack.MyJD_Main_Orders_StatusBtn({
          icon_name: 'review',
        });
        navigate('/profile/my-reviews');
        break;
      case 'returns':
        sendClickTrack.MyJD_Main_Orders_StatusBtn({
          icon_name: 'return',
        });
        // 数量不为0，跳转至售后单列表页Processing tab下，否则跳转all
        if (num && num > 0) {
          navigate('/profile/my-returns?status=1');
        } else {
          navigate('/profile/my-returns?status=0');
        }
        // navigate("/OrderList");
        break;
    }
  };
  // 点击地址
  const handleClickAddress = () => {
    sendClickTrack.MyJD_Main_Addr();
    navigate('/profile/my-address');
  };
  // 关闭个人信息修改弹框
  const onlyClose = (v: string) => {
    if (v == 'info') {
      setInfoOpen(false);
    } else if (v == 'account') {
      setAccountSetOpen(false);
    }
  };

  const handleNumber = (num?: number) => {
    if (_.isNil(num)) {
      return '0';
    } else {
      return Number(num) > 99 ? '99+' : String(num);
    }
  };

  const getOrderStatisInfo = async () => {
    const res = await fetchGetOrderStatisInfo();
    if (res.code === '200') {
      setOrderInfoStatic(res.data);
    }
  };

  const handleInvitePolitely = () => {
    sendClickTrack.MyJD_Main_Invite();
    window.location.href = activityUrl;
  };

  const getInit = async () => {
    const res = await fetchUserCenterAccount([3, 6, 9]);
    if (res.code === '200') {
      const { accountList, userInfo } = res.data;
      setUserInfo(userInfo);
      setIsMember(userInfo?.userTag?.member === MEMBER_STATUS);
      // setIsMember(true)
      setShowMember(userInfo?.extInfo?.member_show_v2);
      accountList.length &&
        accountList.map((item: UserAccountType) => {
          switch (item.type) {
            case 3: // 优惠券数量
              setCouponNum(item.count > 0 ? item.count : 0);
              break;
          }
        });
    }
  };

  const getBindThirdPartyInfo = async () => {
    try {
      const [err, res] = await getUserInfoApi();
      if (res && res.success) {
        setBindThirdParty(res.data.bindOchama);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getActivityStatisInfo = async () => {
    const res = await fetchGetActivityApi();
    if (res && res.code === '200') {
      const data = res?.data;
      const startTime = data?.startTime;
      const endTime = data?.endTime;
      const timestamp = Math.floor(Date.now());
      if (timestamp >= startTime && timestamp < endTime) {
        setShowActivity(true);
        setActivityImage(imageUtil.concatImgUrlWithoutForceHttps(data?.activityImage));
        setActivityUrl(data?.activityUrl);
      } else {
        setShowActivity(false);
      }
    }
  };

  // 查询三方平台绑定账号
  const getThirdPartyAccount = async () => {
    const res = await fetchThirdPartyAccount({ type: 1 });
    if (res && res.code === '200') {
      setIsNeedBind(res.data?.needBind);
    }
  };

  // 获取关联账号的页面链接
  const getLinkUrl = async () => {
    const [err, res]: any = await getThirdAppAuthorizeUrl({
      loginType: LoginType.LoginTypeEnum.OCHAMA,
      redirectUrl: `${origin}/profile`,
    });
    if (res && res.code === '200') {
      setLinkThirdUrl(res.data?.authorizeUri);
    }
  };

  // 关联账号
  const bindThirdPartyAccount = async (params: BindThirdPartyAccountVO) => {
    const res = await fetchBindThirdPartyAccount(params);
    if (res && res.code === '200') {
      getThirdPartyAccount();
    } else {
      UKToast.show({
        content: res.msg,
        icon: 'error',
      });
    }
  };

  //点击加入会员按钮
  const handleClickJoinMember = async () => {
    MEMBER_SENDClICKTRACK.joybuy_plus_entry_Click({
      status: isMember ? '1' : '2',
    });
    if (isMember) {
      router.push('/membership/profile');
    } else {
      router.push('/membership');
    }
  };

  useEffect(() => {
    getInit();
    getOrderStatisInfo();
    getActivityStatisInfo();
    getThirdPartyAccount();
    getLinkUrl();
    getBindThirdPartyInfo();
    sendPv.MyJD_Main();
  }, []);

  useEffect(() => {
    if (code && state && bindThirdParty) {
      bindThirdPartyAccount({ code, state });
    }
  }, [code, state, bindThirdParty]);

  return (
    <>
      {isNeedBind && <LinkTips linkThirdUrl={linkThirdUrl} />}
      <div id='profile_page_container' className={styles.profile_container}>
        <Helmet>
          <title>{`${t('seo.title.profile')} | Joybuy`}</title>
          <meta name='description' content={t('seo.description.profile')} />
        </Helmet>
        <div className={styles.w}>
          <div className={styles.profile}>
            <div className={styles.me}>
              <div className={styles.profile_picture}>
                <div className={styles.picture}>
                  <CDNImage
                    className={styles.pictureImage}
                    src={userInfo?.imageUrl || ''}
                    errorImgType={1}
                    width={60}
                    height={60}
                    alt={'Avatar'}
                  />
                </div>
                <div
                  className={styles.userName}
                  exptag={'exp|' + EXPOSURE_RECORD.MyJD_Main_Name_Expo}
                  data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                >
                  <div className={styles.nickName}>
                    <span className={styles.nickNameTitle}>{userInfo?.nickName}</span>
                    {showMember && isMember && (
                      <CDNImage
                        className={styles.member_icon}
                        errorImgType={1}
                        src={userInfo?.extInfo?.member_icon || ''}
                        width={71}
                        height={32}
                      />
                    )}
                  </div>
                  <div className={styles.editBtns}>
                    <div className={styles.editBtn} onClick={() => handleClickEditProfile()}>
                      <Edit className={styles.iconEdit} width={24} height={24} />
                      {t('profile.page.editProfile')}
                    </div>
                    <div className={styles.divider}></div>
                    <div
                      exptag={'exp|' + EXPOSURE_RECORD.MyJD_Main_Settings_Expo}
                      data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                      className={styles.editBtn}
                      onClick={() => handleClickAccount()}
                    >
                      <Nut className={styles.iconNut} />
                      {t('profile.page.account')}
                    </div>
                    <div className={styles.divider}></div>
                    <div
                      exptag={'exp|' + EXPOSURE_RECORD.MyJD_Main_Addr_Expo}
                      data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                      className={styles.editBtn}
                      onClick={() => handleClickAddress()}
                    >
                      <AddressIcon className={styles.iconAddress} width={24} height={24} />
                      {t('profile.page.addresses')}
                    </div>
                  </div>
                </div>
              </div>
              {showActivity && (
                <div
                  exptag={'exp|' + EXPOSURE_RECORD.MyJD_Main_Invite_Expo}
                  data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                  className={styles.activityImg}
                  onClick={handleInvitePolitely}
                >
                  <img src={activityImage} alt='InvitePolitely' />
                </div>
              )}
            </div>
            {/* 会员楼层 */}
            {showMember && (
              <div className={styles.member}>
                <div className={styles.member_bg}></div>
                <div className={styles.member_box}>
                  <div className={styles.member_content}>
                    <MemberLogo width={170} height={29} />
                    <div>{t('profile.membership.advertisingSlogan')}</div>
                  </div>
                  <button
                    exptag={'exp|' + MEMBER_EXPOSURE_RECORD.joybuy_plus_entry_Exp}
                    data-exptag-config='{"useClick": false, "stay_duration": 0.3, "repeated":false, "area_rate": 0.5}'
                    className={styles.member_join}
                    onClick={handleClickJoinMember}
                  >
                    {isMember ? t('membership.MyPlus') : t('membership.JoinJoybuy')}
                    {isMember && <JumpArrow className={styles.jumpArrow} />}
                  </button>
                </div>
              </div>
            )}
            <div className={styles.order}>
              <div
                exptag={'exp|' + EXPOSURE_RECORD.MyJD_Main_Orders_Expo}
                data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                className={styles.item}
                onClick={() => handleClickMyOrders('order')}
              >
                <OrderIcon className={styles.orderIcon} width={24} height={24} />
                <span className={styles.title}>
                  {t('common.pageName.myOrders')}{' '}
                  <ArrowRight className={styles.arrowRight} width={16} height={16} />
                </span>
              </div>
              <div
                exptag={'exp|' + EXPOSURE_RECORD.MyJD_Main_Orders_StatusBtn_Expo}
                data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                data-exptag-json-param={JSON.stringify({ icon_name: 'unpaid' })}
                className={styles.item}
                onClick={() => handleClickMyOrders('unpaid')}
              >
                <span className={styles.num}>{handleNumber(orderInfoStatic?.unpaidCount)}</span>
                <span className={styles.title}>{t('profile.page.unpaid')}</span>
              </div>
              <div
                exptag={'exp|' + EXPOSURE_RECORD.MyJD_Main_Orders_StatusBtn_Expo}
                data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                data-exptag-json-param={JSON.stringify({ icon_name: 'pending' })}
                className={styles.item}
                onClick={() => handleClickMyOrders('pending')}
              >
                <span className={styles.num}>{handleNumber(orderInfoStatic?.waitRecCount)}</span>
                <span className={styles.title}>{t('profile.page.InProgress')}</span>
              </div>
              <div
                exptag={'exp|' + EXPOSURE_RECORD.MyJD_Main_Orders_StatusBtn_Expo}
                data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                data-exptag-json-param={JSON.stringify({ icon_name: 'review' })}
                className={styles.item}
                onClick={() => handleClickMyOrders('review')}
              >
                <span className={styles.num}>
                  {handleNumber(orderInfoStatic?.waitCommentCount)}
                </span>
                <span className={styles.title}>{t('profile.page.review')}</span>
              </div>
              <div
                exptag={'exp|' + EXPOSURE_RECORD.MyJD_Main_Orders_StatusBtn_Expo}
                data-exptag-config='{"useClick": false, "stay_duration": 0, "repeated":false, "area_rate": 0.01}'
                data-exptag-json-param={JSON.stringify({ icon_name: 'return' })}
                className={styles.item}
                onClick={() => handleClickMyOrders('returns', orderInfoStatic?.afterSaleCount)}
              >
                <span className={styles.num}>{handleNumber(orderInfoStatic?.afterSaleCount)}</span>
                <span className={styles.title}>{t('profile.page.returns')}</span>
              </div>
            </div>

            <div className={styles.moduleList}>
              {itemsList.map((item: Item) => {
                return (
                  <div className={styles.item} key={item.name} onClick={() => item.onClick()}>
                    <div className={styles.title}>
                      <span className={styles.names}>
                        {item.icon}
                        <span className={styles.name}>{item.name}</span>
                        {item.name === t('common.pageName.coupons') && couponNum > 0 && (
                          <span className={styles.num}>{couponNum}</span>
                        )}
                      </span>
                      <ArrowRight className={styles.arrowRight} width={16} height={16} />
                    </div>
                    <div
                      className={styles.subTitle}
                      dangerouslySetInnerHTML={{ __html: item.subTitle as string }}
                    ></div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
      <InfoSetDialog
        open={infoOpen}
        onlyClose={() => onlyClose('info')}
        onSaver={() => {
          getInit();
          setInfoOpen(false);
        }}
      />
      <AccountSetDialog open={accountSetOpen} onlyClose={() => onlyClose('account')} />
      <div className={styles.Recommend}>
        <RecommendClient
          sceneCode={SceneCode.USER_CENTER_PAGE}
          trackParams={{
            RecomSku_Expo: user_EXPOSURE_RECORD.MyJD_Detail_RecomSku_Expo,
            RecomSku_Tab_Expo: user_EXPOSURE_RECORD.MyJD_Main_Detail_RecomSku_Tab_Expo,
            RecomSku_Addcart_Expo: user_EXPOSURE_RECORD.MyJD_Main_Detail_RecomSku_Addcart_Expo,
            productClick: user_sendClickTrack.MyJD_Main_Detail_RecomSku,
            tabClick: user_sendClickTrack.MyJD_Main_Detail_RecomSku_Tab,
            addCartClick: user_sendClickTrack.MyJD_Main_Detail_RecomSku_Addcart,
          }}
        />
      </div>
    </>
  );
});
export default Profile;
